import { cn } from "@/utils/classes";
import { asColor } from "@/utils/format";

/* Prop Types */
interface ProgressProps extends React.ProgressHTMLAttributes<HTMLProgressElement> {
  value: number;
  className?: string;
  staticColorClass?: string;
  disabled?: boolean;
  steps?: number; // If provided, renders as block steps instead of continuous bar
}

/*
 * Progress component
 * This component is used to display progress bar with value
 * Can render as continuous progress bar or discrete steps
 */
export const Progress = ({ value, className, staticColorClass, disabled, steps }: ProgressProps) => {
  const normalColor = staticColorClass ? undefined : asColor(steps ? (value * 100) / steps : value);

  // Render as steps if steps amount is provided
  if (steps) {
    const filledSteps = Math.min(Math.max(Math.round(value), 0), steps);

    return (
      <div className={cn("flex gap-1", className)}>
        {Array.from({ length: steps }, (_, index) => (
          <div
            key={index}
            className={cn("flex-1 h-2 rounded-sm transition-colors duration-200", index < filledSteps ? staticColorClass || "bg-current" : "bg-base-300")}
            style={
              index < filledSteps
                ? ({
                    backgroundColor: disabled ? "#8A8A8E" : staticColorClass ? undefined : normalColor,
                    "--normal-color": normalColor,
                  } as React.CSSProperties & { "--normal-color"?: string })
                : undefined
            }
          />
        ))}
      </div>
    );
  }

  /* Render continuous progress bar */
  return (
    <div className={cn("w-12 h-2 rounded-lg bg-base-300", className)}>
      <div
        className={cn("h-full rounded-lg transition-colors duration-200", staticColorClass, disabled && "progress-bar-disabled")}
        style={
          {
            width: `${value}%`,
            backgroundColor: disabled ? "#8A8A8E" : normalColor,
            "--normal-color": normalColor,
          } as React.CSSProperties & { "--normal-color"?: string }
        }
      ></div>
    </div>
  );
};
